
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";
import { useMutation, useQuery, useResult } from "@vue/apollo-composable";
import { logErrorMessages } from "@vue/apollo-util";

import enUK from "@/locales/home/en-UK.json";

import HeaderBlock from "@/components/HeaderBlock.vue";
import BasicButton from "@/components/BasicButton.vue";
import Tabs from "@/components/Tabs.vue";

import { AdminRoutes } from "@/router/adminRoutes";

import { GET_USER } from "@/graphql/user/queries";
import { SYNC_INVOICES } from "@/graphql/invoice/mutations";
import { SYNC_PERFORMED_ORDERS } from "@/graphql/order/mutations";

type MessageSchema = typeof enUK;

export default defineComponent({
  components: {
    HeaderBlock,
    BasicButton,
    Tabs,
  },

  setup() {
    const { t } = useI18n<[MessageSchema], "en-UK">({
      useScope: "global",
      inheritLocale: true,
      messages: {
        "en-UK": enUK,
      },
    });

    const toast = useToast();

    const { result } = useQuery(GET_USER);

    const userName = useResult(result, "", (data) => {
      return data.user.firstName;
    });

    const tabDefinitions = [
      {
        title: t("titles.generalManager"),
        route: {
          name: AdminRoutes.ADMIN_GENERAL_MANAGER,
        },
      },
      {
        title: "Timesheets",
        route: {
          name: AdminRoutes.ADMIN_TIMESHEETS,
        },
      },
      {
        title: t("titles.projects"),
        route: {
          name: AdminRoutes.ADMIN_PROJECTS,
        },
      },
      {
        title: t("titles.employees"),
        route: {
          name: AdminRoutes.ADMIN_EMPLOYEES,
        },
      },
      {
        title: t("titles.roles"),
        route: {
          name: AdminRoutes.ADMIN_ROLES,
        },
      },
    ];

    const {
      mutate: syncInvoices,
      onDone: onDoneSyncInvoices,
      onError: onErrorSyncInvoices,
      loading: loadingSyncInvoices,
    } = useMutation(SYNC_INVOICES);

    onDoneSyncInvoices(async (result) => {
      if (result.data?.syncInvoices?.status === "ok") {
        toast.success(t("success.syncInvoices"));
        await syncPerformedOrders();
      } else {
        const errorResponse = result.data?.syncInvoices?.error;
        console.log(errorResponse);
        toast.error(t("errors.syncInvoices", { message: errorResponse }));
      }
    });

    onErrorSyncInvoices((error) => {
      logErrorMessages(error);
      toast.error(
        t("errors.syncInvoices", {
          message: "issue with request to backend",
        }),
      );
    });

    const {
      mutate: syncPerformedOrders,
      onDone: onDoneSyncPerformedOrders,
      onError: onErrorSyncPerformedOrders,
      loading: loadingSyncPerformedOrders,
    } = useMutation(SYNC_PERFORMED_ORDERS);

    onDoneSyncPerformedOrders(async (result) => {
      if (result.data?.syncPerformedOrders?.status === "ok") {
        toast.success(t("success.syncPerformedOrders"));
      } else {
        const errorResponse = result.data?.syncPerformedOrders?.error;
        console.log(errorResponse);
        toast.error(
          t("errors.syncPerformedOrders", { message: errorResponse }),
        );
      }
    });

    onErrorSyncPerformedOrders((error) => {
      logErrorMessages(error);
      toast.error(
        t("errors.syncPerformedOrders", {
          message: "issue with request to backend",
        }),
      );
    });

    return {
      t,
      userName,
      tabDefinitions,
      syncInvoices,
      loadingSyncInvoices,
      loadingSyncPerformedOrders,
    };
  },
});
